import {
  boolValue,
  capabilityFunction,
  capabilityNamespace,
  floatValue,
  intValue,
  noneValue,
  optionalType,
  recordType,
  recordValue,
  standardCapabilities,
  stringValue,
  functionType,
  boolType,
  floatType,
  intType,
  stringType,
  type CapabilityBinding,
  type HostCapabilities,
  type Type,
  type Value,
} from '@hollow/script';

import { STUDIO_CAPABILITY_NAMES, type StudioCapabilityName } from './capabilityNames.js';
import type { MapObjectInstance } from '../content/schema/map.js';
import type { LogicAction } from '../runtime/logic/contract.js';
import type { AudioCategory } from '../audio/AudioEngine.js';
import { isKeyDown as engineIsKeyDown } from '../input/inputState.js';
import type { ActionId, KeyBindings } from '../store/engineRuntimeStore.js';

// The v1 host capability bindings, per
// docs/editor-consolidation/hollowscript-capabilities-v1.md. Every capability
// here is wired through HollowScript's own `uses`/`bindCapabilities` model
// (spec.md §6, ADR-0005) - a script that does not `uses` a name cannot call
// it, load-time-enforced by HollowScript itself, not by anything in this
// file. This module only supplies what each granted name is bound to.
//
// Argument shapes are trusted without extra validation beyond the `asX`
// narrowing helpers below: `run()` always type-checks a program before
// executing it (api.ts's own doc comment - "running a program that has not
// first been checked is not a path this function... exposes"), so by the
// time a capability's `invoke` runs, the checker has already guaranteed each
// argument's Value kind matches this capability's declared functionType.
// `asX` exists for a clear failure message in the (checker-should-prevent)
// case that guarantee is ever violated, not as a real security boundary -
// the real boundary is `uses` gating which names a script can reach at all.
//
// Every handle method below returns `Awaitable<T>` (`T | PromiseLike<T>`),
// not a bare `T`: tier 1 runs HollowScript inside a Worker (see
// hollowScriptRuntime.ts), where a "handle" is actually a bridge that posts a
// message to the main thread and awaits a reply - genuinely asynchronous,
// even though the real, same-thread implementation (`liveSceneStore`,
// `ClientLogicRuntime`, `AudioEngine`) is synchronous. Writing every method
// as `Awaitable` lets one capability factory serve both: a real handle's
// plain return value is already a valid `Awaitable<T>`, so
// `tests/unit/scriptingCapabilities.test.ts` calls these factories directly,
// synchronously, with zero wrapping, while `workerCapabilityBridge.ts` calls
// the exact same factories with a genuinely-async remote proxy - one
// capability implementation, not two that could drift.

function asFloat(value: Value): number {
  if (value.kind !== 'Float') throw new Error(`Expected a Float, got ${value.kind}.`);
  return value.value;
}

function asString(value: Value): string {
  if (value.kind !== 'String') throw new Error(`Expected a String, got ${value.kind}.`);
  return value.value;
}

function asInt(value: Value): number {
  if (value.kind !== 'Int') throw new Error(`Expected an Int, got ${value.kind}.`);
  return Number(value.value);
}

const ENTITY_RECORD_TYPE = recordType(
  new Map<string, Type>([
    ['id', stringType],
    ['assetId', stringType],
    ['floor', intType],
    ['x', floatType],
    ['y', floatType],
    ['z', floatType],
  ]),
);

function entityRecord(id: string, assetId: string, floor: number, position: readonly [number, number, number]): Value {
  return recordValue(
    new Map<string, Value>([
      ['id', stringValue(id)],
      ['assetId', stringValue(assetId)],
      ['floor', intValue(BigInt(floor))],
      ['x', floatValue(position[0])],
      ['y', floatValue(position[1])],
      ['z', floatValue(position[2])],
    ]),
  );
}

type Awaitable<T> = T | PromiseLike<T>;

/** What `entitiesCapability` needs - satisfied directly by `liveSceneStore`'s
 * actions (synchronous), or by a Worker-side remote proxy (genuinely async). */
export interface EntitiesHandle {
  spawn(assetId: string, floor: number, position: readonly [number, number, number]): Awaitable<string>;
  getInstance(id: string): Awaitable<MapObjectInstance | undefined>;
  setPosition(id: string, position: readonly [number, number, number]): Awaitable<boolean>;
  remove(id: string): Awaitable<boolean>;
}

/**
 * `uses entities`: spawn/read/write live, running-scene object instances.
 * Bound to `liveSceneStore`'s actions (see that module's own doc comment for
 * why play mode has a live entity store at all now). Deliberately does not
 * expose rotation or params - the minimal surface that makes hot-reloading a
 * script into a live scene demonstrable; a richer surface is a v1.1 addition,
 * not a v1 requirement.
 */
export function entitiesCapability(scene: EntitiesHandle): CapabilityBinding {
  return capabilityNamespace({
    spawn: capabilityFunction(
      'entities.spawn',
      functionType([stringType, intType, floatType, floatType, floatType], stringType),
      async (args) => {
        const [assetId, floor, x, y, z] = args;
        const id = await scene.spawn(asString(assetId!), asInt(floor!), [asFloat(x!), asFloat(y!), asFloat(z!)]);
        return stringValue(id);
      },
    ),
    get: capabilityFunction('entities.get', functionType([stringType], optionalType(ENTITY_RECORD_TYPE)), async (args) => {
      const [id] = args;
      const instance = await scene.getInstance(asString(id!));
      if (!instance) return noneValue;
      return entityRecord(instance.id, instance.assetId, instance.floor, instance.transform.position);
    }),
    setPosition: capabilityFunction(
      'entities.setPosition',
      functionType([stringType, floatType, floatType, floatType], undefined),
      async (args) => {
        const [id, x, y, z] = args;
        await scene.setPosition(asString(id!), [asFloat(x!), asFloat(y!), asFloat(z!)]);
        return noneValue;
      },
    ),
    remove: capabilityFunction('entities.remove', functionType([stringType], undefined), async (args) => {
      const [id] = args;
      await scene.remove(asString(id!));
      return noneValue;
    }),
  });
}

/** What `variablesCapability` needs - satisfied directly by `ClientLogicRuntime`,
 * or by a Worker-side remote proxy. */
export interface VariablesHandle {
  variable(name: string): Awaitable<number>;
  setVariable(name: string, value: number): Awaitable<void>;
}

/**
 * `uses variables`: read/write Logic's per-session variable map. `get`
 * mirrors an authored Logic condition's own `variable` fact; `set`/`add`
 * mirror the `set-variable`/`add-variable` actions, just callable directly
 * rather than only from a matched rule (see `ClientLogicRuntime.setVariable`).
 */
export function variablesCapability(logic: VariablesHandle): CapabilityBinding {
  return capabilityNamespace({
    get: capabilityFunction('variables.get', functionType([stringType], floatType), async (args) => {
      const [name] = args;
      return floatValue(await logic.variable(asString(name!)));
    }),
    set: capabilityFunction('variables.set', functionType([stringType, floatType], undefined), async (args) => {
      const [name, value] = args;
      await logic.setVariable(asString(name!), asFloat(value!));
      return noneValue;
    }),
    add: capabilityFunction('variables.add', functionType([stringType, floatType], undefined), async (args) => {
      const [name, delta] = args;
      const key = asString(name!);
      const current = await logic.variable(key);
      await logic.setVariable(key, current + asFloat(delta!));
      return noneValue;
    }),
  });
}

const AUDIO_CATEGORIES: readonly AudioCategory[] = ['music', 'ambience', 'sfx'];

function asAudioCategory(value: Value): AudioCategory {
  const text = asString(value);
  if ((AUDIO_CATEGORIES as readonly string[]).includes(text)) return text as AudioCategory;
  throw new Error(`audio.play: category must be one of ${AUDIO_CATEGORIES.map((c) => `"${c}"`).join(', ')}, got "${text}".`);
}

/** What `audioCapability` needs - satisfied directly by `AudioEngine.playOneShot`
 * (already async), or by a Worker-side remote proxy. */
export interface AudioHandle {
  playOneShot(url: string, category: AudioCategory): Awaitable<boolean>;
}

/** `uses audio`: play a one-shot sound through the running scene's `AudioEngine`.
 * Awaits the real call and surfaces a `false` result (a bad URL, or an asset
 * that fails to decode - `AudioEngine.playOneShot` never throws for either,
 * it resolves `false`) as a named capability error, matching every other
 * capability that can fail (e.g. `entities.spawn` on an unknown asset id) -
 * an earlier fire-and-forget version silently discarded this, so a typo'd
 * sound path failed with no error, no console line, and nothing for the
 * creator to act on. */
export function audioCapability(engine: AudioHandle): CapabilityBinding {
  return capabilityNamespace({
    play: capabilityFunction('audio.play', functionType([stringType, stringType], undefined), async (args) => {
      const [url, category] = args;
      const source = asString(url!);
      const played = await engine.playOneShot(source, asAudioCategory(category!));
      if (!played) throw new Error(`Failed to play "${source}" - check the URL and that the asset decodes as audio.`);
      return noneValue;
    }),
  });
}

/** What `logicCapability` needs - satisfied directly by `ClientLogicRuntime`,
 * or by a Worker-side remote proxy. */
export interface LogicActionHandle {
  applyAction(action: LogicAction): Awaitable<void>;
}

/**
 * `uses logic`: trigger the same effects an authored Logic rule's `then`
 * action already can, directly - see docs/editor-consolidation/
 * hollowscript-capabilities-v1.md section 3.3 for why this mirrors
 * LogicActionKind 1:1 rather than inventing a separate vocabulary. Not
 * exposing `play-cutscene`: no runtime executes that action kind yet for a
 * rule either (see `isActionKindUnexecuted`), so a scripting path to it
 * would just be a second unimplemented surface.
 */
export function logicCapability(logic: LogicActionHandle): CapabilityBinding {
  async function apply(action: LogicAction): Promise<Value> {
    await logic.applyAction(action);
    return noneValue;
  }
  return capabilityNamespace({
    announce: capabilityFunction('logic.announce', functionType([stringType], undefined), (args) =>
      apply({ kind: 'announce', message: asString(args[0]!) }),
    ),
    emit: capabilityFunction('logic.emit', functionType([stringType], undefined), (args) =>
      apply({ kind: 'emit', event: asString(args[0]!) }),
    ),
    giveItem: capabilityFunction('logic.giveItem', functionType([stringType, intType], undefined), (args) =>
      apply({ kind: 'give-item', name: asString(args[0]!), count: asInt(args[1]!) }),
    ),
    takeItem: capabilityFunction('logic.takeItem', functionType([stringType, intType], undefined), (args) =>
      apply({ kind: 'take-item', name: asString(args[0]!), count: asInt(args[1]!) }),
    ),
    winGame: capabilityFunction('logic.winGame', functionType([], undefined), () => apply({ kind: 'win-game' })),
    loseGame: capabilityFunction('logic.loseGame', functionType([], undefined), () => apply({ kind: 'lose-game' })),
    startQuest: capabilityFunction('logic.startQuest', functionType([stringType, stringType], undefined), (args) =>
      apply({ kind: 'start-quest', questId: asString(args[0]!), title: asString(args[1]!) }),
    ),
    completeQuest: capabilityFunction('logic.completeQuest', functionType([stringType], undefined), (args) =>
      apply({ kind: 'complete-quest', questId: asString(args[0]!) }),
    ),
    failQuest: capabilityFunction('logic.failQuest', functionType([stringType], undefined), (args) =>
      apply({ kind: 'fail-quest', questId: asString(args[0]!) }),
    ),
  });
}

const ACTION_IDS: readonly ActionId[] = [
  'moveForward',
  'moveBack',
  'moveLeft',
  'moveRight',
  'primaryAction',
  'interact',
  'toggleCamera',
];

function asActionId(value: Value): ActionId {
  const text = asString(value);
  if ((ACTION_IDS as readonly string[]).includes(text)) return text as ActionId;
  throw new Error(`input.isActionDown: action must be one of ${ACTION_IDS.map((a) => `"${a}"`).join(', ')}, got "${text}".`);
}

const AXIS_RECORD_TYPE = recordType(
  new Map<string, Type>([
    ['x', floatType],
    ['y', floatType],
  ]),
);

function axisRecord(x: number, y: number): Value {
  return recordValue(
    new Map<string, Value>([
      ['x', floatValue(x)],
      ['y', floatValue(y)],
    ]),
  );
}

/** What `inputCapability` needs - satisfied by `createEngineInputHandle`
 * below (the real, `src/engine/input/inputState.ts`-backed implementation),
 * or a fake for testing. */
export interface InputHandle {
  isActionDown(action: ActionId): Awaitable<boolean>;
  moveAxis(): Awaitable<{ x: number; y: number }>;
}

/**
 * `uses input`: read-only access to the shared engine input state
 * (`src/engine/input/inputState.ts`), the module deferred out of v1 by
 * docs/editor-consolidation/hollowscript-capabilities-v1.md section 3.5
 * specifically because no such shared engine-level input API existed yet to
 * bind to. Now that it does, this mirrors `audioCapability`'s shape exactly:
 * a plain `String` argument validated at call time (`asActionId`, matching
 * `asAudioCategory`'s own pattern) rather than an enum HollowScript's type
 * system has no way to express. Deliberately named/shaped around the
 * existing `ActionId`/`KeyBindings` vocabulary (`store/engineRuntimeStore.ts`)
 * that `FirstPersonController`/`TouchControls` already use, rather than
 * inventing a second one - a script asking "is moveForward down" gets the
 * same answer a player's own rebindable key would. Read-only by design (no
 * `set`/simulate): the minimal useful v1 shape per the deferred item's own
 * framing, matching how `variables`/`entities`/`logic` each started at the
 * smallest surface that proves the mechanism.
 */
export function inputCapability(handle: InputHandle): CapabilityBinding {
  return capabilityNamespace({
    isActionDown: capabilityFunction('input.isActionDown', functionType([stringType], boolType), async (args) => {
      const [action] = args;
      return boolValue(await handle.isActionDown(asActionId(action!)));
    }),
    moveAxis: capabilityFunction('input.moveAxis', functionType([], AXIS_RECORD_TYPE), async () => {
      const { x, y } = await handle.moveAxis();
      return axisRecord(x, y);
    }),
  });
}

/**
 * The real `InputHandle`: each `ActionId` resolved against the CURRENT
 * `KeyBindings` (read live via `getBindings`, so a mid-session rebind takes
 * effect immediately - the same freshness `FirstPersonController`'s own
 * `bindingsRef` gives it) and the shared keyboard state in
 * `src/engine/input/inputState.ts`. `moveAxis` only combines keyboard state
 * today: gamepad and touch already reach the very same shared state (see
 * that module's own doc comment - touch via `dispatchVirtualKey`'s real
 * `KeyboardEvent`s, which this reads no differently from a physical key),
 * but `ActionId`/`KeyBindings` themselves have no gamepad-button mapping
 * yet, matching `FirstPersonController` (keyboard-only) rather than
 * `Platformer2DScene` (which has its own, unrelated ability vocabulary - see
 * this module's own top-of-file doc comment for why the two never shared a
 * naming scheme).
 */
export function createEngineInputHandle(getBindings: () => KeyBindings): InputHandle {
  return {
    isActionDown: (action) => engineIsKeyDown(getBindings()[action]),
    moveAxis: () => {
      const bindings = getBindings();
      return {
        x: (engineIsKeyDown(bindings.moveRight) ? 1 : 0) - (engineIsKeyDown(bindings.moveLeft) ? 1 : 0),
        y: (engineIsKeyDown(bindings.moveForward) ? 1 : 0) - (engineIsKeyDown(bindings.moveBack) ? 1 : 0),
      };
    },
  };
}

/**
 * What `print` writes to. Satisfied by the Code workspace's Output panel,
 * the Preview console, or a Worker-side remote proxy (the tier-1 Worker
 * posts each line back to the main thread over the same capability-call
 * bridge every other handle uses - see `workerCore.ts`). Omitting it from
 * `StudioCapabilityHandles` makes `print` a silent no-op rather than an
 * ungranted name: a script that prints must still type-check and run
 * identically on a host with nowhere to show the text (a published game's
 * boot script, say).
 */
export interface OutputHandle {
  writeLine(text: string): Awaitable<void>;
}

/**
 * The non-deterministic services `clock`/`random` read from (spec.md §6's
 * fixed shapes, supplied to `@hollow/script`'s own `standardCapabilities`
 * rather than reimplemented). Every field is optional; the defaults are the
 * real clock and `Math.random`. A test freezes time by supplying `now`.
 */
export interface StandardServices {
  readonly now?: () => string;
  readonly today?: () => string;
  readonly randomFloat?: () => number;
}

// The `uses` names themselves and the grant-narrowing rule live in the
// dependency-free `capabilityNames.ts` (see its own doc comment for why);
// re-exported here so every existing `capabilities.ts` import keeps working.
export {
  isStudioCapabilityName,
  narrowGrantedCapabilities,
  PROJECT_GRANTABLE_CAPABILITY_NAMES,
  STANDARD_CAPABILITY_NAMES,
  STUDIO_CAPABILITY_NAMES,
} from './capabilityNames.js';
export type { StudioCapabilityName } from './capabilityNames.js';

export interface StudioCapabilityHandles {
  readonly liveScene: EntitiesHandle;
  readonly logic: VariablesHandle & LogicActionHandle;
  readonly audio: AudioHandle;
  /**
   * Optional, unlike the three handles above: `entities`/`variables`/`audio`/
   * `logic` are all required because their handles are session-scoped
   * objects a caller must already own (a live scene store, a running
   * project's `ClientLogicRuntime`, its `AudioEngine`). `input`'s handle
   * wraps `src/engine/input/inputState.ts`, a process-wide singleton with
   * nothing session-scoped to inject - but routing `uses input` through the
   * tier-1 Worker's own capability-call bridge (`workerCore.ts`'s
   * `HandleGroup`, `hollowScriptRuntime.ts`'s `resolveHandle`) is separate
   * follow-up work, not yet done, mirroring how `entities` itself needed a
   * live-scene-store built as its own prerequisite (section 3.4 of the v1
   * design doc). Passing a handle here (e.g. `createEngineInputHandle`
   * above) grants `uses input` for an in-process `run`/`check` call, which
   * is what every test below does; omitting it simply leaves `input`
   * ungranted, exactly like any other capability a caller chooses not to
   * supply - never a runtime crash from a missing handle.
   */
  readonly input?: InputHandle;
  /** Where `print` goes. Optional: absent means `print` is granted but
   * writes nowhere (see `OutputHandle`). */
  readonly output?: OutputHandle;
  /** Clock/random sources for `clock`/`random`; defaults to the real ones. */
  readonly services?: StandardServices;
}

/**
 * Assembles the `HostCapabilities` map `check`/`run` grant a script.
 * `allowed` implements the per-project default-deny grant (task item 4,
 * design doc section 4): omitted, every v1 capability is granted (tier 1's
 * posture - a creator scripting their own project, where HollowScript's own
 * `uses` gating is judged sufficient by the roadmap); pass a project's
 * declared subset once that setting exists to actually restrict what a
 * project's scripts may even ask for.
 */
export function createStudioCapabilities(
  handles: StudioCapabilityHandles,
  allowed: ReadonlySet<StudioCapabilityName> = new Set(STUDIO_CAPABILITY_NAMES),
): HostCapabilities {
  // print/clock/random come from `@hollow/script`'s own `standardCapabilities`
  // (never reimplemented, so this host's grant is shape-identical to the
  // CLI's and to Cascade's), grounded in this host's own services.
  const standard = standardCapabilities({
    writeLine: (text) => {
      void handles.output?.writeLine(text);
    },
    now: handles.services?.now ?? (() => new Date().toISOString()),
    today: handles.services?.today ?? (() => new Date().toISOString().slice(0, 10)),
    randomFloat: handles.services?.randomFloat ?? (() => Math.random()),
  });
  const all: Partial<Record<StudioCapabilityName, CapabilityBinding>> = {
    print: standard.get('print'),
    clock: standard.get('clock'),
    random: standard.get('random'),
    entities: entitiesCapability(handles.liveScene),
    variables: variablesCapability(handles.logic),
    audio: audioCapability(handles.audio),
    logic: logicCapability(handles.logic),
  };
  // `input` is the one optional handle (see `StudioCapabilityHandles.input`'s
  // own doc comment) - only wired in when a caller actually supplies one.
  if (handles.input) all.input = inputCapability(handles.input);
  const capabilities = new Map<string, CapabilityBinding>();
  for (const name of STUDIO_CAPABILITY_NAMES) {
    const binding = all[name];
    if (binding && allowed.has(name)) capabilities.set(name, binding);
  }
  return capabilities;
}
